import {
  intranetQuickAccessResponseSchema,
  overviewLayoutSchema,
  type AuthUser,
  type IntranetQuickAccessItem,
  type OverviewLayout,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, apiFetch } from '@/lib/api';
import {
  buildOverviewCatalog,
  defaultOverviewPaths,
  visibleOverviewTiles,
  type OverviewTile,
} from '@/platform/lib/overview-catalog';
import { hasIntranetProduct } from '@/platform/lib/post-login';

export const overviewQueryKey = ['overview'] as const;
export const overviewShortcutsKey = ['intranet', 'quick-access'] as const;

function currentPaths(layout: OverviewLayout | undefined, user: AuthUser): string[] {
  if (layout?.customized) {
    return layout.paths;
  }
  return defaultOverviewPaths(user);
}

export function useOverviewLayout(user: AuthUser): {
  isPending: boolean;
  customized: boolean;
  paths: string[];
  tiles: OverviewTile[];
  persist: (paths: string[]) => void;
  togglePath: (path: string) => void;
  hasPath: (path: string) => boolean;
  saveError: string | null;
} {
  const queryClient = useQueryClient();
  const layoutQuery = useQuery({
    queryKey: overviewQueryKey,
    queryFn: () => apiFetch('/overview', overviewLayoutSchema),
  });
  const shortcutsQuery = useQuery({
    queryKey: overviewShortcutsKey,
    queryFn: () => apiFetch('/intranet/quick-access', intranetQuickAccessResponseSchema),
    enabled: hasIntranetProduct(user),
  });
  const mutation = useMutation({
    mutationFn: (paths: string[]) =>
      apiFetch('/overview', overviewLayoutSchema, {
        method: 'PUT',
        body: JSON.stringify({ paths }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(overviewQueryKey, data);
    },
  });

  const shortcuts: readonly IntranetQuickAccessItem[] = shortcutsQuery.data?.items ?? [];
  const catalog = buildOverviewCatalog(user, shortcuts);
  const paths = currentPaths(layoutQuery.data, user);
  const tiles = visibleOverviewTiles(paths, catalog);

  function persist(next: string[]) {
    mutation.mutate(next);
  }

  function togglePath(path: string) {
    const next = paths.includes(path) ? paths.filter((item) => item !== path) : [...paths, path];
    persist(next);
  }

  return {
    isPending: layoutQuery.isPending,
    customized: layoutQuery.data?.customized ?? false,
    paths,
    tiles,
    persist,
    togglePath,
    hasPath: (path: string) => paths.includes(path),
    saveError:
      mutation.error instanceof ApiRequestError
        ? mutation.error.error.message
        : mutation.isError
          ? 'Die Übersicht konnte nicht gespeichert werden.'
          : null,
  };
}
